import {
  Box,
  VStack,
  FormControl,
  FormLabel,
  Text,
  Select,
  Input,
  Divider,
  Alert,
  AlertIcon,
  RadioGroup,
  Radio,
  HStack,
  IconButton,
  Button,
  Badge,
} from '@chakra-ui/react';
import { MdAdd, MdDelete } from 'react-icons/md';
import { Controller, useWatch, useFieldArray } from 'react-hook-form';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useMemo, useState, useEffect } from 'react';
import { TaskType, TaskFormData } from '@/lib/sdk/chatbot/types';

interface SkippableConfigSectionProps {
  control: Control<TaskFormData>;
  setValue: UseFormSetValue<TaskFormData>;
  existingTasks: { id: string; name: string; index: number; type: string }[];
  currentTaskIndex?: number;
}

interface ConditionRowProps {
  control: Control<TaskFormData>;
  setValue: UseFormSetValue<TaskFormData>;
  index: number;
  selectableTasks: { id: string; name: string; index: number; type: string }[];
  onRemove: () => void;
  canRemove: boolean;
}

const ConditionRow = ({
  control,
  setValue,
  index,
  selectableTasks,
  onRemove,
  canRemove,
}: ConditionRowProps) => {
  const conditionTaskIndex = useWatch({ control, name: `skipConfig.conditions.${index}.conditionTask` });
  const conditionOperator = useWatch({ control, name: `skipConfig.conditions.${index}.conditionOperator` });
  const conditionValueTask = useWatch({ control, name: `skipConfig.conditions.${index}.conditionValueTask` });

  const [conditionValueType, setConditionValueType] = useState<'static' | 'task'>(
    () => conditionValueTask !== undefined ? 'task' : 'static'
  );

  // Sync toggle when conditionValueTask changes (e.g. form reset on re-open)
  useEffect(() => {
    setConditionValueType(conditionValueTask !== undefined ? 'task' : 'static');
  }, [conditionValueTask]);

  const isConditionTaskNumeric = useMemo(() => {
    if (!conditionTaskIndex) return false;
    const conditionTask = selectableTasks.find(task => task.index === conditionTaskIndex);
    return conditionTask?.type === TaskType.NUMBER;
  }, [conditionTaskIndex, selectableTasks]);

  const showValueInput = conditionTaskIndex && conditionOperator &&
    !['empty', 'not_empty'].includes(conditionOperator);

  return (
    <Box
      bg="bg-canvas"
      p={3}
      rounded="md"
      borderWidth="1px"
      borderColor="border-default"
    >
      <VStack spacing={3} align="stretch">
        <HStack justify="space-between">
          <Badge colorScheme="gray" fontSize="xs">
            Condition {index + 1}
          </Badge>
          {canRemove && (
            <IconButton
              aria-label={`Remove condition ${index + 1}`}
              icon={<MdDelete />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={onRemove}
            />
          )}
        </HStack>

        <FormControl>
          <FormLabel fontSize="sm" color="text-primary">
            Condition Task
          </FormLabel>
          <Controller
            name={`skipConfig.conditions.${index}.conditionTask`}
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                placeholder="Select a task"
                bg="bg-surface"
                borderColor="border-default"
                size="sm"
              >
                {selectableTasks.map((task) => (
                  <option key={task.id} value={task.index}>
                    {task.index}. {task.name}
                  </option>
                ))}
              </Select>
            )}
          />
        </FormControl>

        {conditionTaskIndex && (
          <>
            <FormControl>
              <FormLabel fontSize="sm" color="text-primary">
                Condition
              </FormLabel>
              <Controller
                name={`skipConfig.conditions.${index}.conditionOperator`}
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value ?? ''}
                    placeholder="Select condition"
                    bg="bg-surface"
                    borderColor="border-default"
                    size="sm"
                  >
                    <option value="equals">Equals</option>
                    <option value="not_equals">Does not equal</option>
                    <option value="contains">Contains</option>
                    <option value="not_contains">Does not contain</option>
                    <option value="empty">Is empty</option>
                    <option value="not_empty">Is not empty</option>
                    {isConditionTaskNumeric && (
                      <>
                        <option value="less_than">Less than (&lt;)</option>
                        <option value="less_than_or_equal">Less than or equal (&le;)</option>
                        <option value="greater_than">Greater than (&gt;)</option>
                        <option value="greater_than_or_equal">Greater than or equal (&ge;)</option>
                      </>
                    )}
                  </Select>
                )}
              />
            </FormControl>

            {/* Condition Value - Only show if operator requires a value */}
            {showValueInput && (
              <FormControl>
                <FormLabel fontSize="sm" color="text-primary">
                  Compare Against
                </FormLabel>
                <RadioGroup
                  value={conditionValueType}
                  onChange={(val) => {
                    const newType = val as 'static' | 'task';
                    setConditionValueType(newType);
                    if (newType === 'static') {
                      setValue(`skipConfig.conditions.${index}.conditionValueTask`, undefined);
                    } else {
                      setValue(`skipConfig.conditions.${index}.conditionValue`, undefined);
                    }
                  }}
                  mb={2}
                >
                  <HStack spacing={4}>
                    <Radio value="static" size="sm">Static value</Radio>
                    <Radio value="task" size="sm">Another task</Radio>
                  </HStack>
                </RadioGroup>

                {conditionValueType === 'static' ? (
                  <Controller
                    name={`skipConfig.conditions.${index}.conditionValue`}
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder="e.g., YES, NO, Pickup"
                        bg="bg-surface"
                        borderColor="border-default"
                        size="sm"
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name={`skipConfig.conditions.${index}.conditionValueTask`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                        placeholder="Select comparison task"
                        bg="bg-surface"
                        borderColor="border-default"
                        size="sm"
                      >
                        {selectableTasks
                          .filter(task => task.index !== conditionTaskIndex)
                          .map((task) => (
                            <option key={task.id} value={task.index}>
                              {task.index}. {task.name}
                            </option>
                          ))}
                      </Select>
                    )}
                  />
                )}
              </FormControl>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};

export const SkippableConfigSection = ({
  control,
  setValue,
  existingTasks,
  currentTaskIndex,
}: SkippableConfigSectionProps) => {
  const skipMode = useWatch({ control, name: 'skipConfig.skipMode' });
  const conditionLogic = useWatch({ control, name: 'skipConfig.conditionLogic' });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'skipConfig.conditions',
  });

  // A condition can only read a task that has already been answered
  const selectableTasks = useMemo(
    () => existingTasks.filter(task => currentTaskIndex === undefined || task.index < currentTaskIndex),
    [existingTasks, currentTaskIndex]
  );

  const conditions = useWatch({ control, name: 'skipConfig.conditions' });
  const hasCondition = !!conditions?.some(condition => condition.conditionTask !== undefined);

  return (
    <>
      <Divider />
      <Box>
        <VStack align="stretch" spacing={4}>
          <FormLabel
            color="text-primary"
            fontSize="md"
            fontWeight="medium"
            mb={0}
          >
            Skip Configuration (Optional)
          </FormLabel>
          <Text fontSize="sm" color="text-secondary">
            Configure how this task can be skipped. Optionally add conditions to control when skipping applies.
          </Text>

          <Box
            bg="bg-surface"
            p={4}
            rounded="md"
            borderWidth="1px"
            borderColor="border-default"
          >
            <VStack spacing={4} align="stretch">
              {/* Skip Behavior - always visible */}
              <FormControl>
                <FormLabel fontSize="sm" color="text-primary">
                  Skip Behavior
                </FormLabel>
                <Controller
                  name="skipConfig.skipMode"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? 'manual'}
                      bg="bg-surface"
                      borderColor="border-default"
                      size="sm"
                    >
                      <option value="manual">Show Skip Button (user chooses)</option>
                      <option value="auto">Auto-skip (skip immediately, no user interaction)</option>
                    </Select>
                  )}
                />
                <Text fontSize="xs" color="text-muted" mt={1}>
                  {skipMode === 'auto'
                    ? 'Task will be skipped automatically when the conditions below are met.'
                    : 'A skip button will be shown to the user (optionally only when the conditions are met).'}
                </Text>
              </FormControl>

              {/* Auto-skip warning when no condition is set */}
              {skipMode === 'auto' && !hasCondition && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  At least one condition is required for auto-skip. Add one below.
                </Alert>
              )}

              <FormControl>
                <FormLabel fontSize="sm" color="text-primary">
                  Conditions
                  {skipMode === 'auto' && <Text as="span" color="red.400" ml={1}>*</Text>}
                </FormLabel>
                <Text fontSize="xs" color="text-muted" mb={2}>
                  {skipMode === 'auto'
                    ? 'Required: add conditions on earlier tasks that control when this task is skipped.'
                    : 'Optional: add conditions on earlier tasks that determine when to show the skip button. Leave empty to always show it.'}
                </Text>

                {fields.length > 1 && (
                  <HStack spacing={2} mb={3}>
                    <Text fontSize="sm" color="text-primary">Match</Text>
                    <Controller
                      name="skipConfig.conditionLogic"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={field.value ?? 'and'}
                          bg="bg-surface"
                          borderColor="border-default"
                          size="sm"
                          width="auto"
                        >
                          <option value="and">ALL</option>
                          <option value="or">ANY</option>
                        </Select>
                      )}
                    />
                    <Text fontSize="sm" color="text-primary">
                      of the following conditions
                    </Text>
                  </HStack>
                )}

                <VStack spacing={3} align="stretch">
                  {fields.map((field, index) => (
                    <ConditionRow
                      key={field.id}
                      control={control}
                      setValue={setValue}
                      index={index}
                      selectableTasks={selectableTasks}
                      onRemove={() => remove(index)}
                      canRemove={fields.length > 1}
                    />
                  ))}
                </VStack>

                <Button
                  leftIcon={<MdAdd />}
                  size="sm"
                  variant="outline"
                  mt={3}
                  onClick={() => append({
                    conditionTask: undefined,
                    conditionOperator: undefined,
                    conditionValue: undefined,
                    conditionValueTask: undefined,
                  })}
                >
                  Add condition
                </Button>

                {fields.length > 1 && (
                  <Text fontSize="xs" color="text-muted" mt={2}>
                    {conditionLogic === 'or'
                      ? 'This task can be skipped when at least one condition is met.'
                      : 'This task can be skipped only when every condition is met.'}
                  </Text>
                )}
              </FormControl>
            </VStack>
          </Box>
        </VStack>
      </Box>
    </>
  );
};
